import _ from "lodash";
import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { connect } from "react-redux";
import { useI18n } from "Hooks";
import { ENTITY_TYPE } from "MyCalendar/modules/MyCalendarModule/MyCalendarConstants";
import { getFormattedUserName } from "Utils/GetFormattedUserName";

import {
  Button,
  Avatar,
  Tooltip,
  RadioButton,
  Checkbox,
  IconButton,
} from "@toddle-edu/ds-web";
import { Popover, ArrowContainer } from "@toddle-edu/react-tiny-popover";
import {
  AddOutlined,
  DeleteOutlined,
  PencilLineOutlined,
} from "@toddle-edu/ds-icons";
import { STUDENT } from "Constants/stringConstants";
import { StudentAlertsWrapper } from "AppComponents/StudentAlerts";

import classes from "./RecipientSelector.scss";
import classNames from "classnames";

import AddRecipientModal from "../AddRecipientModal";
import UserParticipantList from "../UserParticipantList";
import {
  getUserEventCreationAccessLevel,
  getSelectedSchoolDetailsForCalendar,
  getSelectedAcademicYearIdForSchool,
  isBigOrgFFEnabledAndStaffUser,
} from "MyCalendar/modules/Utils";

import { useGetUserCountByEntity } from "MyCalendar/components/EventModalV2/hooks";
import { getOrganizationId } from "modules/CommonSelectors";

import { getIsHolidayEvent, setValue } from "../../../modules/Utils";
import { SelectParticipantsModal } from "AppComponents/SharedEntities";

const PARTICIPANT_USER_TYPES = {
  STAFF: "common:staff_member_other",
  STUDENT: "common:student_plural",
  FAMILY_MEMBER: "common:family_member_plural",
};

const PARTICIPANT_SELECTION_LIST_TYPES = {
  GRADES: "common:grade_plural",
  CLASSES: "common:class_plural",
  CUSTOM: "common:custom",
};

const PARTICIPANT_GARDE_TYPE_MAPPING = {
  GRADES: "common:grade_plural",
};

const styles = {
  arrowContainer: {
    position: "relative",
    left: "40px",
    maxHeight: "180px",
    width: "488px",
    padding: "16px 16px",
    border: "1px solid var(--border-secondary)",
    borderRadius: "12px",
    backgroundColor: "var(--surface-primary-enabled)",
    boxShadow: "0px 8px 16px 0px rgba(0, 0, 0, 0.1)",
    overflowY: "auto",
  },
  arrow: {
    top: "0.5px",
    left: "200px",
    width: "20px",
    height: "20px",
    transform: "rotate(-45deg)",
    borderLeft: "none",
    borderTop: "1px solid var(--border-secondary)",
    borderRight: "1px solid var(--border-secondary)",
    borderRadius: "0 4px 0 0",
    backgroundColor: "var(--surface-primary-enabled)",
    zIndex: 1,
  },
};

const FooterSelectCount = ({ participantSelections, selectedListType }) => {
  const { t } = useI18n();

  const selectedCount = _.size(participantSelections[selectedListType]);

  const getCountText = () => {
    switch (selectedListType) {
      case ENTITY_TYPE.GRADES:
        return t("common:grade_selected_with_count", { count: selectedCount });
      case ENTITY_TYPE.CLASSES:
        return t("common:selected_course_count", { count: selectedCount });
      case ENTITY_TYPE.CUSTOM:
        return t("common:count_member_selected_other", {
          count: selectedCount,
        });
      default:
        return `${selectedCount} selected`;
    }
  };

  return <div className={classes.footerLeft}>{getCountText()}</div>;
};

const RecipientPopover = props => {
  const {
    entities,
    entityType,
    handleRemoveEntity,
    selectedListType,
    participantSelections,
    isFullScreenMode,
  } = props;
  const [isOpen, setIsOpen] = useState(false);

  const closePopover = () => {
    setIsOpen(false);
  };

  const isEntitiesSelected = !_.isEmpty(entities);

  const popoverTextClasses = classNames(
    {
      [classes.popoverText]: isEntitiesSelected,
    },
    { "text-label-s": !isFullScreenMode },
    { "text-label": isFullScreenMode }
  );

  const isBigOrgStaffUser = isBigOrgFFEnabledAndStaffUser();

  return !isBigOrgStaffUser ? (
    <Popover
      isOpen={isOpen}
      isClosable={false}
      onClickOutside={closePopover}
      onCloseButtonClick={closePopover}
      positions={["bottom"]}
      containerClassName={classes.popoverContainerParent}
      content={({ position, childRect, popoverRect }) => (
        <ArrowContainer
          position={position}
          childRect={childRect}
          popoverRect={popoverRect}
          arrowStyle={styles.arrow}
          arrowSize={10}
        >
          <ArrowContainer
            position={position}
            childRect={childRect}
            popoverRect={popoverRect}
            style={styles.arrowContainer}
          >
            <div className={classes.popoverContainer}>
              <div
                className={classNames(
                  "text-heading-6",
                  classes.countHeaderPreview
                )}
              >
                <FooterSelectCount
                  participantSelections={participantSelections}
                  selectedListType={selectedListType}
                />
              </div>
              {entityType === ENTITY_TYPE.CUSTOM ? (
                <UserParticipantList
                  participantSelections={participantSelections}
                  handleRemoveEntity={handleRemoveEntity}
                />
              ) : (
                _.map(entities, entity => {
                  const {
                    id,
                    name,
                    title,
                    acronym,
                    profileImage,
                    type,
                    alertTypes = [],
                  } = entity;

                  const isUser = entityType === ENTITY_TYPE.CUSTOM;
                  const fullName = isUser
                    ? getFormattedUserName({ user: entity, type })
                    : "";
                  const label = isUser ? fullName : title ?? name ?? acronym;

                  const avatarSrc = isUser ? profileImage : "";
                  const handleItemClick = e => {
                    e.stopPropagation();
                    handleRemoveEntity(id);
                  };

                  return (
                    <div key={id} className={classes.popoverItemContainer}>
                      <div className={classes.avatarLabelContainer}>
                        <Avatar
                          dsVersion="2.0"
                          id={id}
                          name={label}
                          size="medium"
                          shape="square"
                          src={avatarSrc}
                          className={classes.avatar}
                        />
                        <StudentAlertsWrapper
                          studentId={id}
                          alertTypes={alertTypes}
                          hideAlerts={type !== STUDENT}
                          className={"text-body"}
                        >
                          {label}
                        </StudentAlertsWrapper>
                      </div>
                      <IconButton
                        dsVersion="2.0"
                        size="x-small"
                        variant="neutral"
                        type="plain"
                        icon={<DeleteOutlined />}
                        onClick={handleItemClick}
                      />
                    </div>
                  );
                })
              )}
            </div>
          </ArrowContainer>
        </ArrowContainer>
      )}
    >
      <div className={popoverTextClasses}>
        <span
          className={classes.popoverContent}
          onClick={() => setIsOpen(!isOpen)}
        >
          <FooterSelectCount
            participantSelections={participantSelections}
            selectedListType={entityType}
          />
        </span>
      </div>
    </Popover>
  ) : (
    <div className={popoverTextClasses}>
      <FooterSelectCount
        participantSelections={participantSelections}
        selectedListType={entityType}
      />
    </div>
  );
};

const UserTypeCheckBoxes = ({
  t,
  selectedUserTypes,
  onUserTypeChange,
  isFullScreenMode,
  testId,
}) => {
  const allUserTypes = _.keys(PARTICIPANT_USER_TYPES);
  const isAllSelected = _.isEqual(
    _.sortBy(selectedUserTypes),
    _.sortBy(allUserTypes)
  );
  const isIndeterminate = !_.isEmpty(selectedUserTypes) && !isAllSelected;

  const handleAllChange = () => {
    if (isAllSelected) {
      // Deselect all - pass empty array
      onUserTypeChange([], true);
    } else {
      // Select all - pass all user types
      onUserTypeChange(allUserTypes, true);
    }
  };

  const sectionTitleClasses = classNames(
    { "text-label": isFullScreenMode },
    { "text-label": !isFullScreenMode }
  );

  const userTypeCheckboxsClasses = classNames(
    { [classes.userTypeCheckBoxes]: true },
    { [classes.userTypeCheckBoxesMoreGap]: isFullScreenMode }
  );

  return (
    <>
      <div className={sectionTitleClasses}>
        {t("calendar:select_participants")}
      </div>
      <div className={userTypeCheckboxsClasses}>
        {isFullScreenMode ? (
          <div className={classes.userTypeCheckboxContainer}>
            <Checkbox
              size={isFullScreenMode ? "medium" : "small"}
              isChecked={isAllSelected}
              isIndeterminate={isIndeterminate}
              onChange={handleAllChange}
              className={classes.userTypeCheckbox}
              testId={`${testId}-All`}
            >
              <span className={"text-body"}>
                {t(isFullScreenMode ? "common:all_members" : "common:all")}
              </span>
            </Checkbox>
          </div>
        ) : null}
        {_.map(PARTICIPANT_USER_TYPES, (userTypeLocale, userType) => {
          const isSelected = _.includes(selectedUserTypes, userType);
          const handleUserTypeChange = () => {
            onUserTypeChange(userType, !isSelected);
          };
          return (
            <Checkbox
              size={"medium"}
              isChecked={isSelected}
              onChange={handleUserTypeChange}
              className={classes.userTypeCheckbox}
              testId={`${testId}-${userType}`}
              key={userType}
            >
              <span className={"text-body"}>{t(userTypeLocale)}</span>
            </Checkbox>
          );
        })}
      </div>
    </>
  );
};

const SelectionListTypeRadioButtons = ({
  t,
  selectedListType,
  onListTypeChange,
  isHolidayEvent,
  userCanCreateHolidaysAndWholeSchoolEvents,
  isFullScreenMode,
  setSelectedListType,
  eventModalV2,
  testId,
}) => {
  // Filter out the ENTITY_TYPE.GRADES from PARTICIPANT_SELECTION_LIST_TYPES when user only has class level create access
  const filteredParticipantSelectionListTypes = !userCanCreateHolidaysAndWholeSchoolEvents
    ? _.omit(PARTICIPANT_SELECTION_LIST_TYPES, ENTITY_TYPE.GRADES)
    : PARTICIPANT_SELECTION_LIST_TYPES;

  const PARTICIPANT_SELECTION_LIST_MAPPING = isHolidayEvent
    ? PARTICIPANT_GARDE_TYPE_MAPPING
    : filteredParticipantSelectionListTypes;

  const selectionListTypeClasses = classNames(
    { "text-label": isFullScreenMode },
    { "text-label": !isFullScreenMode }
  );

  const selectionListTypeRadioButtonsClasses = classNames(
    classes.selectionListTypeRadioButtons,
    {
      [classes.selectionListTypeRadioButtonsMoreGap]: isFullScreenMode,
    }
  );

  useEffect(() => {
    // checking if it is in edit mode as eventModalV2?.eventId is present
    if (_.isEmpty(selectedListType)) {
      setSelectedListType(_.first(_.keys(PARTICIPANT_SELECTION_LIST_MAPPING)));
    }
  }, [PARTICIPANT_SELECTION_LIST_MAPPING, eventModalV2?.eventType]);

  return (
    <>
      <div className={selectionListTypeClasses}>
        {t("calendar:select_participants_by")}
      </div>
      <div className={selectionListTypeRadioButtonsClasses}>
        {_.map(
          PARTICIPANT_SELECTION_LIST_MAPPING,
          (selectionListTypeLocale, selectionListType) => {
            const handleListTypeChange = () => {
              onListTypeChange(selectionListType);
            };

            return (
              <div
                className={classes.selectionListRadioContainer}
                key={selectionListType}
              >
                <RadioButton
                  size={"medium"}
                  checked={selectedListType === selectionListType}
                  onChange={handleListTypeChange}
                  className={classes.selectionListRadio}
                  testId={`${testId}-${selectionListType}`}
                >
                  <span className={"text-body"}>
                    {t(selectionListTypeLocale)}
                  </span>
                </RadioButton>
              </div>
            );
          }
        )}
      </div>
    </>
  );
};

const ButtonTooltipWrapper = ({ disabled, disabledTooltip, children }) => {
  if (disabled) {
    return <Tooltip tooltip={disabledTooltip}>{children}</Tooltip>;
  }

  return children;
};

const USER_TYPE_MAPPING = {
  Staff: "STAFF",
  Student: "STUDENT",
  FamilyMember: "FAMILY_MEMBER",
};

const RecipientSelector = props => {
  const {
    onConfirm,
    eventModalV2,
    updateEventModalV2,
    isFullScreenMode,
    organizationId,
    academicYearId,
    userCanCreateHolidaysAndWholeSchoolEvents,
    currentCurriculumProgramId,
    testId = "",
    reciepentTitleLocaleKey = "calendar:who_should_be_invited_to_this_event",
  } = props;
  const { t } = useI18n();
  /*
    participantsSelection = {
      "GRADES": [<grade_objects>],
      "CLASSES": [<class_objects>],
      "CUSTOM": [<user_objects>],
    }
    */
  const [participantSelections, setParticipantSelections] = useState(
    eventModalV2?.participants
  );

  // check if any keys in participantSelections have non-empty arrays
  const isRecipientSelected = _.some(
    participantSelections,
    selection => !_.isEmpty(selection)
  );

  const selectedUserTypes = useMemo(() => eventModalV2?.sharedWith || [], [
    eventModalV2?.sharedWith,
  ]);

  const _setSelectedUserTypes = setValue("sharedWith", updateEventModalV2);
  const selectedListType = eventModalV2?.entityType || "";
  const setSelectedUserTypes = useCallback(
    userTypes => {
      _setSelectedUserTypes(userTypes);
      if (selectedListType === ENTITY_TYPE.CUSTOM) {
        // Filter participantSelections based on selected user types
        // This is to ensure that only the selected user types are included in the participant selections
        const updatedParticipantSelections = {
          ...participantSelections,
          [ENTITY_TYPE.CUSTOM]: _.chain(participantSelections)
            .get(ENTITY_TYPE.CUSTOM, [])
            .filter(user => {
              return _.includes(
                userTypes,
                USER_TYPE_MAPPING[_.get(user, "__typename")]
              );
            })
            .value(),
        };
        setParticipantSelections(updatedParticipantSelections);
        updateEventModalV2({
          participants: updatedParticipantSelections,
          totalParticipantUserCount: _.size(
            updatedParticipantSelections[ENTITY_TYPE.CUSTOM]
          ),
        });
      }
    },
    [
      _setSelectedUserTypes,
      selectedListType,
      participantSelections,
      updateEventModalV2,
    ]
  );

  const setSelectedListType = setValue("entityType", updateEventModalV2);

  const handleUserTypeChange = useCallback(
    (keyOrKeys, isCheckedOrBulk) => {
      const prevSelected = selectedUserTypes || [];

      if (Array.isArray(keyOrKeys)) {
        // Handle bulk update (from "All" checkbox)
        setSelectedUserTypes(keyOrKeys);
      } else {
        // Handle individual checkbox update
        const key = keyOrKeys;
        const isChecked = isCheckedOrBulk;
        setSelectedUserTypes(
          isChecked
            ? _.concat(prevSelected, key)
            : _.filter(prevSelected, selectedKey => selectedKey !== key)
        );
      }
    },
    [selectedUserTypes, setSelectedUserTypes]
  );

  const handleListTypeChange = useCallback(
    key => {
      setParticipantSelections({});
      setSelectedListType(key);
      updateEventModalV2({
        entityType: key,
        participants: {},
        totalParticipantUserCount: 0,
      });
    },
    [setSelectedListType, updateEventModalV2]
  );

  const [isAddRecipientModalOpen, setIsAddRecipientModalOpen] = useState(false);

  const handleOpenAddRecipientModal = () => {
    setIsAddRecipientModalOpen(true);
  };

  const handleCloseAddRecipientModal = () => {
    setIsAddRecipientModalOpen(false);
  };

  // Unified SelectParticipantsModal: replaces per-mode modals for big-org staff
  const shouldUseSelectParticipantsModal = isBigOrgFFEnabledAndStaffUser();

  const isAllUserGroupsSelected = _.isEqual(
    _.size(PARTICIPANT_USER_TYPES),
    _.size(selectedUserTypes)
  );

  const { totalParticipants } = useGetUserCountByEntity({
    participantSelections,
    selectedListType,
    sharedWith: selectedUserTypes,
  });

  useEffect(() => {
    // Keeping redux state for totalParticipantUserCount updated
    updateEventModalV2({
      totalParticipantUserCount: totalParticipants,
    });
  }, [totalParticipants, updateEventModalV2]);

  const updateFormValues = newStates => {
    onConfirm({
      participantSelections:
        newStates?.participantSelections || participantSelections,
      selectedUserTypes: newStates?.selectedUserTypes || selectedUserTypes,
      selectedListType: newStates?.selectedListType || selectedListType,
      totalParticipantUserCount: totalParticipants,
    });
  };

  const updateFuncRef = useRef(null);
  updateFuncRef.current = updateFormValues;

  const handleConfirmSelectParticipants = useCallback(
    ({ selectedGradeList, selectedCourseList }) => {
      const updatedSelections = {
        ...participantSelections,
        [selectedListType]:
          selectedListType === ENTITY_TYPE.GRADES
            ? selectedGradeList || []
            : selectedCourseList || [],
      };
      setParticipantSelections(updatedSelections);
      updateFuncRef.current({ participantSelections: updatedSelections });
    },
    [participantSelections, selectedListType]
  );

  const handleRemoveEntity = id => {
    const updatedParticipantSelections = _.assign({}, participantSelections, {
      [selectedListType]: _.reject(participantSelections[selectedListType], {
        id,
      }),
    });

    setParticipantSelections(updatedParticipantSelections);
    updateEventModalV2({
      participants: updatedParticipantSelections,
      totalParticipantUserCount: _.size(
        updatedParticipantSelections[selectedListType]
      ),
    });
  };

  const popoverTextClasses = classNames(
    { "text-label-s": !isFullScreenMode },
    { "text-label": isFullScreenMode }
  );

  const leftContentHeaderClasses = classNames(classes.leftContentHeader, {
    [classes.leftContentHeaderFullScreenPadding]: isFullScreenMode,
  });

  const eventUID = eventModalV2?.eventUID;
  const isHolidayEvent = getIsHolidayEvent(eventUID);

  const reciepentButtonText = t(
    `${
      isRecipientSelected
        ? "announcement:edit_entity"
        : "announcement:add_entity"
    }`,
    {
      entity:
        selectedListType === ENTITY_TYPE.CUSTOM
          ? t("announcement:recipient_other")
          : t(PARTICIPANT_SELECTION_LIST_TYPES[selectedListType]),
    }
  );
  return (
    <>
      <div className={leftContentHeaderClasses}>
        {isFullScreenMode ? (
          <h6 className={classNames(classes.fullScreenTitle)}>
            {t(reciepentTitleLocaleKey)}
            <div className={classNames(classes.required, "text-label")}>*</div>
          </h6>
        ) : null}
        <UserTypeCheckBoxes
          t={t}
          selectedUserTypes={selectedUserTypes}
          onUserTypeChange={handleUserTypeChange}
          isFullScreenMode={isFullScreenMode}
          testId={`${testId}-userTypeCheckboxes`}
        />
        <SelectionListTypeRadioButtons
          t={t}
          selectedListType={selectedListType}
          onListTypeChange={handleListTypeChange}
          isHolidayEvent={isHolidayEvent}
          userCanCreateHolidaysAndWholeSchoolEvents={
            userCanCreateHolidaysAndWholeSchoolEvents
          }
          isFullScreenMode={isFullScreenMode}
          setSelectedListType={setSelectedListType}
          eventModalV2={eventModalV2}
          testId={`${testId}-selectionListTypeRadioButtons`}
        />
        <hr className={classes.hr} />

        <div className={classes.recipientActionsContainer}>
          <ButtonTooltipWrapper disabled={_.isEmpty(selectedUserTypes)}>
            <Button
              dsVersion="2.0"
              variant="neutral"
              type={"fill"}
              icon={
                isRecipientSelected ? <PencilLineOutlined /> : <AddOutlined />
              }
              size={isFullScreenMode ? "medium" : "small"}
              onClick={handleOpenAddRecipientModal}
              disabled={_.isEmpty(selectedUserTypes)}
              testId={`${testId}-${reciepentButtonText?.replaceAll(" ", "-")}`}
            >
              <div className={classes.buttonText}>{reciepentButtonText}</div>
            </Button>
          </ButtonTooltipWrapper>
          <div>
            {isRecipientSelected ? (
              <RecipientPopover
                participantSelections={participantSelections}
                entities={participantSelections[selectedListType]}
                entityType={selectedListType}
                handleRemoveEntity={handleRemoveEntity}
                isFullScreenMode={isFullScreenMode}
                selectedListType={selectedListType}
              />
            ) : (
              <div className={popoverTextClasses}>
                <FooterSelectCount
                  participantSelections={participantSelections}
                  selectedListType={selectedListType}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      {isAddRecipientModalOpen ? (
        shouldUseSelectParticipantsModal &&
        selectedListType !== ENTITY_TYPE.CUSTOM ? (
          <SelectParticipantsModal
            isOpen={isAddRecipientModalOpen}
            onClose={handleCloseAddRecipientModal}
            onConfirm={handleConfirmSelectParticipants}
            organizationId={organizationId}
            academicYearId={academicYearId}
            selectedUserTypes={selectedUserTypes}
            initialSelectedGradeList={
              participantSelections?.[ENTITY_TYPE.GRADES] ?? []
            }
            initialSelectedCourseList={
              participantSelections?.[ENTITY_TYPE.CLASSES] ?? []
            }
            initialSelectedMemberList={
              participantSelections?.[ENTITY_TYPE.CUSTOM] ?? []
            }
            isRecipientSelected={isRecipientSelected}
            testId={testId}
            activeMode={
              selectedListType === "CUSTOM" ? "MEMBERS" : selectedListType
            }
            enableMembersMode={true}
          />
        ) : (
          <AddRecipientModal
            sharedWith={selectedUserTypes}
            entityType={selectedListType}
            updateFormValues={updateFormValues}
            isOpen={isAddRecipientModalOpen}
            onClose={handleCloseAddRecipientModal}
            userGroupLabel={PARTICIPANT_USER_TYPES[selectedListType]}
            isAllUserGroupsSelected={isAllUserGroupsSelected}
            isRecipientSelected={isRecipientSelected}
            participantSelections={participantSelections}
            setParticipantSelections={setParticipantSelections}
            totalParticipants={totalParticipants}
            currentCurriculumProgramId={currentCurriculumProgramId}
            eventModalV2={eventModalV2}
            testId={testId}
          />
        )
      ) : null}
    </>
  );
};

const localMapStateToProps = state => {
  const organizationId = getOrganizationId(state);
  const academicYearId = getSelectedAcademicYearIdForSchool(state);
  const { currentCurriculumProgramId } = getSelectedSchoolDetailsForCalendar(
    state
  );

  const userEventCreationAccessLevel = getUserEventCreationAccessLevel({
    currentCurriculumProgramId,
    alsoCheckClassLevelPermission: false,
  });

  const userCanCreateHolidaysAndWholeSchoolEvents = _.includes(
    ["ACCOUNT_LEVEL", "PROGRAM_LEVEL"],
    userEventCreationAccessLevel
  );

  return {
    organizationId,
    academicYearId,
    userCanCreateHolidaysAndWholeSchoolEvents,
    currentCurriculumProgramId,
  };
};

export default connect(localMapStateToProps, {})(RecipientSelector);
